import React, { useEffect, useState } from "react";
import { useTrialBalanceStore } from "../../../config/stores";
import { Card, Space, Alert, Spin, Form, Typography } from "antd";
import TrialBalanceTable from "./TrialBalanceTable";
import { find, isEmpty } from "lodash";
import AppButton from "../../../components/common/AppButton";
import AppInput from "../../../components/common/AppInput";
import { AiOutlineFileExcel, AiOutlineReload, AiOutlineFilePdf } from "react-icons/ai";
import dayjs from "dayjs";
import generateTrialBalancePdf from "../../../config/services/trialBalancePdf";

const { Title } = Typography;

const TrialBalance = () => {
  const trialBalanceStore = useTrialBalanceStore();
  const [trialBalance, setTrialBalance] = useState(null);
  const [totals, setTotals] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [form] = Form.useForm();
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs());

  const getDefaultContext = () => {
    const startDateValue = startDate
      ? dayjs(startDate).format("YYYY-MM-DD")
      : dayjs().startOf("month").format("YYYY-MM-DD");
    const endDateValue = endDate
      ? dayjs(endDate).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");
    return { startDate: startDateValue, endDate: endDateValue };
  };

  useEffect(() => {
    const defaultContext = getDefaultContext();
    form.setFieldsValue({
      startDate: dayjs(defaultContext.startDate),
      endDate: dayjs(defaultContext.endDate),
    });
  }, []);

  useEffect(() => {
    const defaultContext = getDefaultContext();
    const existingData = find(trialBalanceStore.trialBalance, (records) => {
      return (
        records?.startDate === defaultContext.startDate &&
        records?.endDate === defaultContext.endDate
      );
    });

    if (existingData) {
      const data = existingData?.data;
      const trialBalanceData = data?.data?.trialBalance || data?.trialBalance;
      const totalsData = data?.data?.totals || data?.totals;
      
      if (trialBalanceData || totalsData) {
        setTrialBalance({ trialBalance: trialBalanceData, totals: totalsData });
        setTotals(totalsData);
      } else {
        setTrialBalance(data);
        setTotals(data?.totals);
      }
      setHasLoaded(true);
    }
  }, [trialBalanceStore.trialBalance, startDate, endDate]);

  const handleGetTrialBalance = () => {
    const defaultContext = getDefaultContext();
    setHasLoaded(false);
    trialBalanceStore.getTrialBalance(defaultContext).finally(() => {
      setHasLoaded(true);
    });
  };

  const reloadTrialBalance = () => {
    handleGetTrialBalance();
  };

  const downloadTrialBalance = () => {
    const defaultContext = getDefaultContext();
    const fileName = `TRIAL_BALANCE_${defaultContext.startDate}_TO_${defaultContext.endDate}.xlsx`;
    trialBalanceStore.downloadTrialBalance(defaultContext, fileName);
  };

  const downloadTrialBalancePdf = () => {
    if (trialBalance && totals) {
      const defaultContext = getDefaultContext();
      generateTrialBalancePdf(trialBalance, defaultContext.endDate);
    }
  };

  const isLoading = trialBalanceStore.gettingTrialBalance && !hasLoaded;
  const hasError = !isEmpty(trialBalanceStore.trialBalanceError);
  const hasData = trialBalance?.trialBalance?.length > 0;
  const currentStartDate = startDate
    ? dayjs(startDate).format("YYYY-MM-DD")
    : dayjs().startOf("month").format("YYYY-MM-DD");
  const currentEndDate = endDate
    ? dayjs(endDate).format("YYYY-MM-DD")
    : dayjs().format("YYYY-MM-DD");

  return (
    <div>
      <Card
        bordered={false}
        title={
          <div>
            <Title level={4} style={{ margin: 0 }}>
              TRIAL BALANCE
            </Title>
            <div style={{ fontSize: "14px", color: "#666", marginTop: "4px" }}>
              Period: {currentStartDate} to {currentEndDate}
            </div>
          </div>
        }
        extra={
          <Space>
            <AppButton
              text="RELOAD"
              loadingText="RELOADING..."
              loading={trialBalanceStore.gettingTrialBalance}
              iconBefore={<AiOutlineReload />}
              onClick={reloadTrialBalance}
            />
            <AppButton
              text="DOWNLOAD EXCEL"
              danger
              type="dashed"
              loadingText="DOWNLOADING..."
              iconBefore={<AiOutlineFileExcel />}
              loading={trialBalanceStore.downloadingTrialBalance}
              onClick={downloadTrialBalance}
              disabled={!hasData}
            />
            <AppButton
              text="DOWNLOAD PDF"
              type="primary"
              loadingText="GENERATING..."
              iconBefore={<AiOutlineFilePdf />}
              onClick={downloadTrialBalancePdf}
              disabled={!hasData}
            />
          </Space>
        }
      >
        <Form
          form={form}
          layout="inline"
          onFinish={handleGetTrialBalance}
          style={{ marginBottom: "16px" }}
        >
          <AppInput
            name="startDate"
            label="Start Date"
            type="date"
            rules={[{ required: true, message: "Please select a start date" }]}
            inputAttributes={{
              onChange: (date) => {
                setStartDate(date);
              },
            }}
          />
          <AppInput
            name="endDate"
            label="End Date"
            type="date"
            rules={[{ required: true, message: "Please select an end date" }]}
            inputAttributes={{
              onChange: (date) => {
                setEndDate(date);
              },
              disabledDate: (current) =>
                current && startDate && current.isBefore(dayjs(startDate), "day"),
            }}
          />
          <Form.Item>
            <AppButton
              text="GET TRIAL BALANCE"
              loadingText="Loading..."
              loading={trialBalanceStore.gettingTrialBalance}
              htmlType="submit"
            />
          </Form.Item>
        </Form>
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
            <div style={{ marginTop: "16px" }}>Loading trial balance...</div>
          </div>
        ) : hasError ? (
          <Alert
            type="error"
            showIcon
            message="Error Loading Trial Balance"
            description={
              <div>
                <div style={{ marginBottom: "8px" }}>
                  <strong>Error:</strong>{" "}
                  {trialBalanceStore.trialBalanceError?.response?.data?.server?.message ||
                   trialBalanceStore.trialBalanceError?.response?.data?.message ||
                   trialBalanceStore.trialBalanceError?.message ||
                   "Unable to load trial balance."}
                </div>
                {trialBalanceStore.trialBalanceError?.response?.status === 404 && (
                  <div style={{ marginTop: "12px", padding: "12px", backgroundColor: "#fff3cd", borderRadius: "4px", fontSize: "12px" }}>
                    <div style={{ marginBottom: "8px" }}>
                      <strong>404 - Resource Not Found</strong>
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                      The endpoint <code>/api/v1/finance/trial-balance</code> was not found.
                    </div>
                    <div style={{ marginTop: "8px" }}>
                      <strong>Backend Setup Required:</strong>
                      <ul style={{ marginTop: "4px", marginLeft: "20px", padding: 0 }}>
                        <li>Ensure the <code>trialBalanceRouter</code> is mounted in your main finance router:</li>
                        <li style={{ fontFamily: "monospace", marginTop: "4px" }}>
                          <code>router.use('/trial-balance', trialBalanceRouter);</code>
                        </li>
                        <li style={{ marginTop: "8px" }}>Check that the route file is imported and registered</li>
                        <li>Verify the server is running and the route is accessible</li>
                      </ul>
                    </div>
                    <div style={{ marginTop: "8px", fontSize: "11px", color: "#666" }}>
                      Check the browser console (F12) for the exact request URL and error details.
                    </div>
                  </div>
                )}
                {trialBalanceStore.trialBalanceError?.response?.status !== 404 && (
                  <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
                    Status: {trialBalanceStore.trialBalanceError?.response?.status} - {trialBalanceStore.trialBalanceError?.response?.statusText}
                  </div>
                )}
              </div>
            }
          />
        ) : !hasData ? (
          <Alert
            type="info"
            showIcon
            message="No Trial Balance Data"
            description={`No trial balance data available for the period ${currentStartDate} to ${currentEndDate}.`}
          />
        ) : (
          <TrialBalanceTable trialBalanceData={trialBalance} totals={totals} />
        )}
      </Card>
    </div>
  );
};

export default TrialBalance;

